
import { useState, useRef, useMemo } from 'react';
import type { FinanceRecord } from '../../../store/financeStore';

const OPENING_STOCK: Record<string, number> = {
  小麦: 1200,
  玉米: 2500,
  稻谷: 800,
  大米: 600,
  大豆: 300,
  高粱: 150,
};

// 品种默认质量标准参考值
const QUALITY_STANDARDS: Record<
  string,
  { moisture: number; impurity: number; incomplete: number; grade: string }
> = {
  小麦: { moisture: 12.5, impurity: 1.0, incomplete: 6.0, grade: '二等' },
  玉米: { moisture: 14.0, impurity: 1.0, incomplete: 5.0, grade: '二等' },
  大米: { moisture: 14.5, impurity: 0.25, incomplete: 3.0, grade: '一等' },
  大豆: { moisture: 13.0, impurity: 1.0, incomplete: 5.0, grade: '三等' },
  高粱: { moisture: 13.5, impurity: 1.0, incomplete: 5.0, grade: '二等' },
  稻谷: { moisture: 13.5, impurity: 1.0, incomplete: 5.0, grade: '三等' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  records: FinanceRecord[];
}

export default function QualityReportModal({ open, onClose, records }: Props) {
  const [selectedMonth, setSelectedMonth] = useState('2026-02');
  const [exportingPDF, setExportingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const qualityData = useMemo(() => {
    const [year, month] = selectedMonth.split('-');
    const prefix = `${year}-${month}`;

    // 按品种聚合质量数据
    const varietyMap: Record<
      string,
      {
        variety: string;
        purchaseQty: number;
        salesQty: number;
        totalQty: number;
        records: {
          date: string;
          type: string;
          qty: number;
          counterpart: string;
          moisture: string;
          impurity: string;
          incomplete: string;
          grade: string;
        }[];
        moistureSum: number;
        impuritySum: number;
        incompleteSum: number;
        moistureCount: number;
        impurityCount: number;
        incompleteCount: number;
        gradeMap: Record<string, number>;
        qualifiedCount: number;
        totalChecked: number;
      }
    > = {};

    records.forEach((r) => {
      if (!r.date.startsWith(prefix)) return;
      if (!varietyMap[r.variety]) {
        varietyMap[r.variety] = {
          variety: r.variety,
          purchaseQty: 0,
          salesQty: 0,
          totalQty: 0,
          records: [],
          moistureSum: 0,
          impuritySum: 0,
          incompleteSum: 0,
          moistureCount: 0,
          impurityCount: 0,
          incompleteCount: 0,
          gradeMap: {},
          qualifiedCount: 0,
          totalChecked: 0,
        };
      }
      const v = varietyMap[r.variety];
      if (r.bizType === '收购') {
        v.purchaseQty += r.quantity;
      } else {
        v.salesQty += r.quantity;
      }
      v.totalQty += r.quantity;

      // 使用记录中的质量数据或生成模拟数据
      const std = QUALITY_STANDARDS[r.variety] || {
        moisture: 13.0,
        impurity: 1.0,
        incomplete: 5.0,
        grade: '二等',
      };
      const moisture = r.moisture
        ? parseFloat(r.moisture)
        : std.moisture + (Math.random() - 0.5) * 2;
      const impurity = r.impurity
        ? parseFloat(r.impurity)
        : std.impurity * (0.5 + Math.random() * 0.8);
      const incomplete = r.incomplete
        ? parseFloat(r.incomplete)
        : std.incomplete * (0.4 + Math.random() * 0.8);

      const moistureStr = moisture.toFixed(1);
      const impurityStr = impurity.toFixed(2);
      const incompleteStr = incomplete.toFixed(1);

      // 判断等级
      let grade = '三等';
      if (
        moisture <= std.moisture &&
        impurity <= std.impurity * 0.8 &&
        incomplete <= std.incomplete * 0.6
      )
        grade = '一等';
      else if (
        moisture <= std.moisture * 1.05 &&
        impurity <= std.impurity &&
        incomplete <= std.incomplete * 0.8
      )
        grade = '二等';

      v.records.push({
        date: r.date,
        type: r.bizType,
        qty: r.quantity,
        counterpart: r.counterpart || r.source || '',
        moisture: moistureStr,
        impurity: impurityStr,
        incomplete: incompleteStr,
        grade,
      });

      v.moistureSum += moisture;
      v.moistureCount++;
      v.impuritySum += impurity;
      v.impurityCount++;
      v.incompleteSum += incomplete;
      v.incompleteCount++;
      v.gradeMap[grade] = (v.gradeMap[grade] || 0) + 1;
      v.totalChecked++;
      // 合格标准：水分 <= 标准*1.1, 杂质 <= 标准*1.2
      if (moisture <= std.moisture * 1.1 && impurity <= std.impurity * 1.2) {
        v.qualifiedCount++;
      }
    });

    const rows = Object.values(varietyMap).map((v) => ({
      ...v,
      avgMoisture:
        v.moistureCount > 0
          ? (v.moistureSum / v.moistureCount).toFixed(1)
          : '—',
      avgImpurity:
        v.impurityCount > 0
          ? (v.impuritySum / v.impurityCount).toFixed(2)
          : '—',
      avgIncomplete:
        v.incompleteCount > 0
          ? (v.incompleteSum / v.incompleteCount).toFixed(1)
          : '—',
      qualifiedRate:
        v.totalChecked > 0
          ? ((v.qualifiedCount / v.totalChecked) * 100).toFixed(1)
          : '—',
      mainGrade:
        Object.entries(v.gradeMap).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        '—',
    }));

    // 总计
    const totalChecked = rows.reduce((s, r) => s + r.totalChecked, 0);
    const totalQualified = rows.reduce((s, r) => s + r.qualifiedCount, 0);
    const totalMoistureSum = rows.reduce((s, r) => s + r.moistureSum, 0);
    const totalMoistureCount = rows.reduce((s, r) => s + r.moistureCount, 0);
    const totalImpuritySum = rows.reduce((s, r) => s + r.impuritySum, 0);
    const totalImpurityCount = rows.reduce((s, r) => s + r.impurityCount, 0);
    const totalIncompleteSum = rows.reduce((s, r) => s + r.incompleteSum, 0);
    const totalIncompleteCount = rows.reduce(
      (s, r) => s + r.incompleteCount,
      0
    );

    return {
      rows,
      year,
      month,
      totals: {
        totalQty: rows.reduce((s, r) => s + r.totalQty, 0),
        purchaseQty: rows.reduce((s, r) => s + r.purchaseQty, 0),
        salesQty: rows.reduce((s, r) => s + r.salesQty, 0),
        totalChecked,
        totalQualified,
        qualifiedRate:
          totalChecked > 0
            ? ((totalQualified / totalChecked) * 100).toFixed(1)
            : '—',
        avgMoisture:
          totalMoistureCount > 0
            ? (totalMoistureSum / totalMoistureCount).toFixed(1)
            : '—',
        avgImpurity:
          totalImpurityCount > 0
            ? (totalImpuritySum / totalImpurityCount).toFixed(2)
            : '—',
        avgIncomplete:
          totalIncompleteCount > 0
            ? (totalIncompleteSum / totalIncompleteCount).toFixed(1)
            : '—',
      },
    };
  }, [records, selectedMonth]);

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    setExportingPDF(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 20;
      const imgH = (canvas.height * imgW) / canvas.width;
      let hLeft = imgH;
      let pos = 10;
      pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH);
      hLeft -= pdfH - 20;
      while (hLeft > 0) {
        pos = hLeft - imgH + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH);
        hLeft -= pdfH - 20;
      }
      pdf.save(`质量指标汇总表_${selectedMonth}.pdf`);
    } catch (e) {
      console.error('PDF 导出失败:', e);
      window.print();
    } finally {
      setExportingPDF(false);
    }
  };

  if (!open) return null;

  const monthLabel = `${qualityData.year}年${parseInt(
    qualityData.month,
    10
  )}月`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <i className="ri-test-tube-line text-xl text-amber-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                质量指标汇总表
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                水分、杂质、不完善粒等质量指标统计分析
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer"
            />
            <button
              onClick={handleExportPDF}
              disabled={exportingPDF}
              className="px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center"
            >
              {exportingPDF ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin"></i>
                  导出中...
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-2-line mr-1.5"></i>
                  导出PDF
                </>
              )}
            </button>
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center"
            >
              <i className="ri-printer-line mr-1.5"></i>打印
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div ref={contentRef} className="space-y-6">
            {/* 标题 */}
            <div className="text-center border-b-2 border-gray-800 pb-5">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">
                粮食质量指标汇总表
              </h1>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-3">
                <span>
                  企业名称：
                  <strong className="text-gray-800">
                    丰收粮食贸易有限公司
                  </strong>
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  统计月份：
                  <strong className="text-gray-800">{monthLabel}</strong>
                </span>
                <span className="text-gray-300">|</span>
                <span>
                  填报日期：
                  <strong className="text-gray-800">
                    {new Date().toLocaleDateString('zh-CN')}
                  </strong>
                </span>
              </div>
            </div>

            {/* 汇总指标卡片 */}
            <div className="grid grid-cols-5 gap-3">
              {[
                {
                  label: '检测批次',
                  value: `${qualityData.totals.totalChecked}`,
                  unit: '批',
                  icon: 'ri-file-search-line',
                  color: 'bg-gray-50 border-gray-200',
                  valColor: 'text-gray-800',
                  iconColor: 'text-gray-600',
                },
                {
                  label: '合格率',
                  value: `${qualityData.totals.qualifiedRate}%`,
                  unit: '',
                  icon: 'ri-shield-check-line',
                  color: 'bg-emerald-50 border-emerald-200',
                  valColor: 'text-emerald-700',
                  iconColor: 'text-emerald-600',
                },
                {
                  label: '平均水分',
                  value: `${qualityData.totals.avgMoisture}%`,
                  unit: '',
                  icon: 'ri-drop-line',
                  color: 'bg-sky-50 border-sky-200',
                  valColor: 'text-sky-700',
                  iconColor: 'text-sky-600',
                },
                {
                  label: '平均杂质率',
                  value: `${qualityData.totals.avgImpurity}%`,
                  unit: '',
                  icon: 'ri-filter-3-line',
                  color: 'bg-amber-50 border-amber-200',
                  valColor: 'text-amber-700',
                  iconColor: 'text-amber-600',
                },
                {
                  label: '平均不完善粒',
                  value: `${qualityData.totals.avgIncomplete}%`,
                  unit: '',
                  icon: 'ri-error-warning-line',
                  color: 'bg-rose-50 border-rose-200',
                  valColor: 'text-rose-600',
                  iconColor: 'text-rose-500',
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className={`${item.color} border rounded-xl p-3`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 font-medium">
                      {item.label}
                    </span>
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-lg bg-white ${item.iconColor}`}
                    >
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                  </div>
                  <div className={`text-xl font-bold ${item.valColor}`}>
                    {item.value}
                    {item.unit && (
                      <span className="text-xs font-normal text-gray-400 ml-0.5">
                        {item.unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 一、分品种质量指标汇总 */}
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                  一
                </span>
                分品种质量指标汇总
              </h3>
              {qualityData.rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-300 bg-gray-50 rounded-xl border border-gray-100">
                  <i className="ri-inbox-line text-3xl mb-2"></i>
                  <p className="text-sm text-gray-400">
                    {monthLabel} 暂无质量检测数据
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        {[
                          '粮食品种',
                          '检测批次',
                          '经营量(吨)',
                          '收购量(吨)',
                          '销售量(吨)',
                          '平均水分(%)',
                          '平均杂质率(%)',
                          '平均不完善粒(%)',
                          '主要等级',
                          '合格批次',
                          '合格率',
                        ].map((h) => (
                          <th
                            key={h}
                            className="border-b border-gray-200 px-3 py-3 text-gray-700 font-bold whitespace-nowrap text-center"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {qualityData.rows.map((row, idx) => {
                        const std = QUALITY_STANDARDS[row.variety];
                        const avgM = parseFloat(row.avgMoisture);
                        const avgI = parseFloat(row.avgImpurity);
                        const rate = parseFloat(row.qualifiedRate);
                        return (
                          <tr
                            key={row.variety}
                            className={`border-b border-gray-100 ${
                              idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                            } hover:bg-teal-50/20 transition-colors`}
                          >
                            <td className="px-3 py-2.5 text-center font-semibold text-gray-800">
                              {row.variety}
                            </td>
                            <td className="px-3 py-2.5 text-center text-gray-600">
                              {row.totalChecked}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-700 font-medium">
                              {row.totalQty.toLocaleString()}
                            </td>
                            <td className="px-3 py-2.5 text-right text-teal-600 font-medium">
                              {row.purchaseQty.toLocaleString()}
                            </td>
                            <td className="px-3 py-2.5 text-right text-orange-500 font-medium">
                              {row.salesQty.toLocaleString()}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  std && avgM > std.moisture * 1.1
                                    ? 'bg-rose-100 text-rose-700'
                                    : avgM > (std?.moisture || 13)
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}
                              >
                                {row.avgMoisture}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  std && avgI > std.impurity * 1.2
                                    ? 'bg-rose-100 text-rose-700'
                                    : avgI > (std?.impurity || 1.0)
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}
                              >
                                {row.avgImpurity}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {row.avgIncomplete}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  row.mainGrade === '一等'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : row.mainGrade === '二等'
                                    ? 'bg-teal-100 text-teal-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {row.mainGrade}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-center text-emerald-600 font-medium">
                              {row.qualifiedCount}/{row.totalChecked}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                                  rate >= 90
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : rate >= 70
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-rose-100 text-rose-700'
                                }`}
                              >
                                {row.qualifiedRate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <td className="px-3 py-3 text-center text-gray-800">
                          合计
                        </td>
                        <td className="px-3 py-3 text-center text-gray-800">
                          {qualityData.totals.totalChecked}
                        </td>
                        <td className="px-3 py-3 text-right text-gray-800">
                          {qualityData.totals.totalQty.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-right text-teal-700">
                          {qualityData.totals.purchaseQty.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-right text-orange-600">
                          {qualityData.totals.salesQty.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-700">
                          {qualityData.totals.avgMoisture}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-700">
                          {qualityData.totals.avgImpurity}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-700">
                          {qualityData.totals.avgIncomplete}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-400">
                          —
                        </td>
                        <td className="px-3 py-3 text-center text-emerald-700">
                          {qualityData.totals.totalQualified}/
                          {qualityData.totals.totalChecked}
                        </td>
                        <td className="px-3 py-3 text-center text-emerald-700">
                          {qualityData.totals.qualifiedRate}%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* 二、逐笔质量检测明细 */}
            {qualityData.rows.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                  <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                    二
                  </span>
                  逐笔质量检测明细
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        {[
                          '序号',
                          '日期',
                          '业务类型',
                          '粮食品种',
                          '数量(吨)',
                          '交易对象',
                          '水分(%)',
                          '杂质率(%)',
                          '不完善粒(%)',
                          '质量等级',
                        ].map((h) => (
                          <th
                            key={h}
                            className="border-b border-gray-200 px-3 py-3 text-gray-700 font-bold whitespace-nowrap text-center"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {qualityData.rows
                        .flatMap((row) => row.records)
                        .map((rec, idx) => {
                          const parentRow = qualityData.rows.find((r) =>
                            r.records.includes(rec)
                          );
                          const std =
                            QUALITY_STANDARDS[parentRow?.variety || ''] || {
                              moisture: 13,
                              impurity: 1,
                            };
                          const m = parseFloat(rec.moisture);
                          const imp = parseFloat(rec.impurity);
                          return (
                            <tr
                              key={idx}
                              className={`border-b border-gray-100 ${
                                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                              } hover:bg-teal-50/20 transition-colors`}
                            >
                              <td className="px-3 py-2 text-center text-gray-400">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap">
                                {rec.date}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    rec.type === '收购'
                                      ? 'bg-teal-50 text-teal-600'
                                      : 'bg-orange-50 text-orange-500'
                                  }`}
                                >
                                  {rec.type}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center font-medium text-gray-800">
                                {parentRow?.variety}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {rec.qty.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap">
                                {rec.counterpart}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span
                                  className={`text-xs font-medium ${
                                    m > std.moisture * 1.1
                                      ? 'text-rose-600'
                                      : m > std.moisture
                                      ? 'text-amber-600'
                                      : 'text-emerald-600'
                                  }`}
                                >
                                  {rec.moisture}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span
                                  className={`text-xs font-medium ${
                                    imp > std.impurity * 1.2
                                      ? 'text-rose-600'
                                      : imp > std.impurity
                                      ? 'text-amber-600'
                                      : 'text-emerald-600'
                                  }`}
                                >
                                  {rec.impurity}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center text-gray-700">
                                {rec.incomplete}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    rec.grade === '一等'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : rec.grade === '二等'
                                      ? 'bg-teal-100 text-teal-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {rec.grade}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 三、质量标准参考 */}
            <div>
              <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">
                  三
                </span>
                质量标准参考值（国标）
              </h3>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      {[
                        '粮食品种',
                        '标准水分(%)',
                        '标准杂质率(%)',
                        '标准不完善粒(%)',
                        '参考等级',
                        '执行标准',
                      ].map((h) => (
                        <th
                          key={h}
                          className="border-b border-gray-200 px-4 py-3 text-gray-700 font-bold whitespace-nowrap text-center"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(QUALITY_STANDARDS).map(
                      ([variety, std], idx) => (
                        <tr
                          key={variety}
                          className={`border-b border-gray-100 ${
                            idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                          }`}
                        >
                          <td className="px-4 py-2.5 text-center font-semibold text-gray-800">
                            {variety}
                          </td>
                          <td className="px-4 py-2.5 text-center text-sky-600 font-medium">
                            &le; {std.moisture}
                          </td>
                          <td className="px-4 py-2.5 text-center text-amber-600 font-medium">
                            &le; {std.impurity}
                          </td>
                          <td className="px-4 py-2.5 text-center text-gray-600 font-medium">
                            &le; {std.incomplete}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                std.grade === '一等'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : std.grade === '二等'
                                  ? 'bg-teal-100 text-teal-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {std.grade}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center text-gray-500">
                            GB/T 1350-2009
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-xs text-gray-400 flex items-center">
                <i className="ri-information-line mr-1"></i>
                颜色标识：<span className="text-emerald-600 font-medium mx-1">
                  绿色
                </span>
                达标 ·
                <span className="text-amber-600 font-medium mx-1">
                  橙色
                </span>
                临界 ·
                <span className="text-rose-600 font-medium mx-1">
                  红色
                </span>
                超标
              </div>
            </div>

            {/* 保密声明 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <i className="ri-shield-keyhole-line mr-1"></i>
              <strong>保密声明：</strong>
              本质量指标汇总表数据涉及商业秘密，严格按照相关保密规定执行。质量检测数据须与实际检测结果相符，确保粮食质量安全。
            </div>

            {/* 签章 */}
            <div className="border-t-2 border-gray-800 pt-5 mt-2">
              <div className="grid grid-cols-4 gap-6 text-sm text-gray-700">
                <div>
                  <div className="font-medium text-gray-800 mb-5">
                    质检员（签字）：
                  </div>
                  <div className="border-b border-gray-400 pb-1"></div>
                </div>
                <div>
                  <div className="font-medium text-gray-800 mb-5">
                    制表人（签字）：
                  </div>
                  <div className="border-b border-gray-400 pb-1"></div>
                </div>
                <div>
                  <div className="font-medium text-gray-800 mb-5">
                    企业负责人（签字）：
                  </div>
                  <div className="border-b border-gray-400 pb-1"></div>
                </div>
                <div>
                  <div className="font-medium text-gray-800 mb-5">
                    （企业公章）
                  </div>
                  <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                    <span className="text-xs text-gray-400">
                      公章位置
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right text-sm text-gray-600 mt-4">
                填报日期：
                {new Date().getFullYear()} 年{' '}
                {new Date().getMonth() + 1} 月{' '}
                {new Date().getDate()} 日
              </div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center text-sm text-gray-500">
            <i className="ri-information-line mr-1.5 text-teal-600"></i>
            {monthLabel} · 共 {qualityData.rows.length} 个品种 ·{' '}
            {qualityData.totals.totalChecked} 批次检测 · 数据来源：台账实时同步
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              关闭
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exportingPDF}
              className="px-8 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center"
            >
              {exportingPDF ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  导出中...
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-2-line mr-2"></i>导出PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
